import clsx from "clsx"
import { forwardRef, type ReactNode } from "react"

import type { SX } from "../../theme"
import { AnimatedElement } from "../AnimatedElement"
import { Box } from "../Box"
import { Text } from "../Text"
import styles from "./FormField.module.css"
import { FormFieldError } from "./FormFieldError"

export interface FormFieldProps {
  children?: ReactNode
  error?: boolean | string
  description?: string
  descriptionPosition?: "top" | "bottom"
  sx?: SX
  id?: string
  label?: string
}

export const FormField = forwardRef<HTMLDivElement, FormFieldProps>(
  function FormField(props, ref) {
    const {
      children,
      error = false,
      sx,
      description,
      descriptionPosition = "top",
      id,
      label,
    } = props

    const hasTopDescription = description && descriptionPosition === "top"
    const hasBottomDescription = description && descriptionPosition === "bottom"

    return (
      <div ref={ref} className={styles.formField} style={sx}>
        {label && (
          <Text
            variant="emphasized"
            component="label"
            htmlFor={props.id}
            sx={hasTopDescription ? undefined : { marginBottom: 4 }}
          >
            {props.label}
          </Text>
        )}

        {hasTopDescription && (
          <FormFieldDescription
            description={description}
            sx={{ marginBottom: 4 }}
          />
        )}

        {children}
        <AnimatedFormError error={error} id={id} />

        {hasBottomDescription && (
          <FormFieldDescription
            description={description}
            sx={{ marginTop: 4 }}
          />
        )}
      </div>
    )
  },
)

export type RoundedSide = "all" | "top" | "bottom"

export interface FormInputFieldProps extends FormFieldProps {
  disabled?: boolean
  size?: "small" | "medium" | "large"
  roundedSide?: RoundedSide
}

export const FormInputField = forwardRef<HTMLDivElement, FormInputFieldProps>(
  function FormInputField(props, ref) {
    const {
      children,
      disabled = false,
      error = false,
      sx,
      size = "large",
      description,
      descriptionPosition,
      roundedSide = "all",
      id,
      label,
    } = props

    return (
      <FormField
        id={id}
        error={error}
        label={label}
        description={description}
        descriptionPosition={descriptionPosition}
        ref={ref}
        sx={sx}
      >
        <div
          className={clsx(
            styles.field,
            styles[`fieldSize-${size}`],
            styles[`fieldColor-${getState(disabled, error)}`],
            styles[`roundedSide-${roundedSide}`],
          )}
        >
          {children}
        </div>
      </FormField>
    )
  },
)

interface FormFieldDescriptionProps {
  description: string
  sx?: SX
}

function FormFieldDescription(props: FormFieldDescriptionProps) {
  const { description, sx } = props
  return (
    <Text component="span" color="grey11" sx={sx}>
      {description}
    </Text>
  )
}

interface AnimatedFormErrorProps {
  error?: string | boolean
  id?: string
}

export function AnimatedFormError(props: AnimatedFormErrorProps) {
  const { error, id } = props

  return (
    <AnimatedElement animation={{ name: "expand-vertical" }}>
      {typeof error === "string" && (
        <Box flexDirection="column">
          <FormFieldError id={id}>{error}</FormFieldError>
        </Box>
      )}
    </AnimatedElement>
  )
}

function getState(disabled: boolean, error: FormFieldProps["error"]) {
  if (disabled) return "disabled"
  if (error) return "error"
  return "normal"
}
